import { reactive } from "vue"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import {
    SyncScope,
    SyncSession,
    WorkspaceSourceGraph,
    plainToContentFileRef,
    plainToSyncSession,
    plainToWorkspaceComparison,
} from "./SyncSessionModel"
import { SyncSessionState } from "./SyncSessionState"
import { useSyncSessionClient } from "./SyncSessionClient"
import { useSyncServiceApi } from "./SyncServiceApi"
import { useTelemetry } from "@webapp/util/Telemetry"
import { buildLivingWindowGroups, RelationshipCategory } from "./LivingWindowGroups"
import { Sync, SyncSource, SyncSourceDetails, Workspace } from "@webapp/rd/ResearchDataModel"
import { useWorkspaceStore } from "@webapp/rd/WorkspaceStore"
import { useSyncStore } from "@webapp/rd/SyncStore"
import { useDataGroupStore } from "@webapp/gp/DataGroupStore"
import { DataGroup, DataGroupType } from "@webapp/gp/GroupAdminModel"
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore } from "@webapp/util/AsyncDataStore"
import { Environment } from "@webapp/util/Environment"

export const useSyncSessionStore = defineStore("syncSessionStore", () => {
    const syncApi = useSyncServiceApi()
    const client = useSyncSessionClient()
    // Captured in setup: useTelemetry -> inject() only resolves here, not inside later action calls.
    const { trackException } = useTelemetry()

    const asyncSessions = new Map<string, AsyncData<SyncSession>>()
    const sessionStates = reactive(new Map<string, SyncSessionState>())
    // In-flight changes-upload aborts, keyed by session -- used by cancelSyncAsync. Not reactive.
    const uploadAborts = new Map<string, AbortController>()

    function getState(sessionId: string | undefined) {
        return sessionId ? sessionStates.get(sessionId) : undefined
    }

    function requireState(sessionId: string) {
        const state = sessionStates.get(sessionId)
        if (!state) {
            throw new Error(`No session state: ${sessionId}`)
        }
        return state
    }

    function getSession(sessionId: string | undefined): SyncSession | undefined {
        // Read via the reactive store proxy so in-place cache updates are tracked.
        return sessionId ? useSyncSessionStore().asyncSessions.get(sessionId)?.data : undefined
    }

    function getOrCreateState(sessionId: string) {
        let state = sessionStates.get(sessionId)
        if (!state) {
            state = reactive(new SyncSessionState(sessionId)) as SyncSessionState
            sessionStates.set(sessionId, state)
        }
        return state
    }

    function dropSession(sessionId: string | undefined) {
        if (!sessionId) {
            return
        }
        const store = useSyncSessionStore()
        store.sessionStates.delete(sessionId)
        store.asyncSessions.delete(sessionId)
    }

    function addSessionsToStore(sessions: SyncSession[], requestTime: DateTime) {
        const store = useSyncSessionStore()
        const sessionMap = AsyncDataStore.createItemMap(sessions, "SyncSession", s => s, s => s.id!)
        AsyncDataStore.addItemMapToStore(store.asyncSessions, "SyncSession", sessionMap, requestTime)
        return sessionMap
    }

    async function createSessionAsync() {
        const requestTime = DateTime.utc()
        const agentVersion = Environment.get('APP_VERSION') || 'dev'
        const p = await syncApi.postPlainAsync('sessions', { agent: 'Web', agentVersion })
        const sessionMap = addSessionsToStore([plainToSyncSession(p)], requestTime)
        const session = sessionMap.values().next().value!
        getOrCreateState(session.id!)
        return session.id!
    }

    async function ensureSessionLoadedAsync(sessionId: string) {
        if (!asyncSessions.get(sessionId)?.data) {
            try {
                const requestTime = DateTime.utc()
                const p = await client.getSessionAsync(sessionId)
                addSessionsToStore([p], requestTime)
            }
            catch {
                return undefined
            }
        }
        getOrCreateState(sessionId)
        return getSession(sessionId)
    }

    async function refetchSessionAsync(sessionId: string) {
        const requestTime = DateTime.utc()
        const p = await client.getSessionAsync(sessionId)
        addSessionsToStore([p], requestTime)
    }

    async function refreshSessionAsync(sessionId: string) {
        const requestTime = DateTime.utc()
        const p = await client.refreshSessionAsync(sessionId)
        addSessionsToStore([p], requestTime)
    }

    async function destroySessionAsync(sessionId: string) {
        await client.destroySessionAsync(sessionId)
    }

    async function uploadFileAsync(
        sessionId: string,
        file: File,
        onProgress?: (loaded: number, total: number) => void,
        signal?: AbortSignal,
    ) {
        const query = { sourceModifiedDate: DateTime.fromMillis(file.lastModified).toUTC().toISO()! }
        await syncApi.uploadFileAsync(`sessions/${sessionId}/file`, file, onProgress, signal, query)
        await refetchSessionAsync(sessionId)
    }

    async function setWorkspaceAsync(sessionId: string, workspaceId: string) {
        const requestTime = DateTime.utc()
        const session = await client.setWorkspaceAsync(sessionId, workspaceId)
        addSessionsToStore([session], requestTime)
    }

    async function loadSourceDetailsAsync(sessionId: string, scope: SyncScope) {
        requireState(sessionId).sourceDetails = await client.loadSourceDetailsAsync(sessionId, scope)
    }

    // Compare the uploaded source against an existing workspace's content (the re-sync "wrong file" guard).
    async function compareToWorkspaceAsync(sessionId: string, workspaceId: string) {
        const p = await syncApi.postPlainAsync(`sessions/${sessionId}/compare`, undefined, { workspaceId })
        return plainToWorkspaceComparison(p)
    }

    async function loadLivingWindowGraphAsync(sessionId: string) {
        const state = requireState(sessionId)
        const graph = await client.loadLivingWindowGraphAsync(sessionId)
        state.sourceGraph = graph
        state.livingStartPersonId = graph.startPersonId
        setDefaultLivingWindowPersons(state, graph)
    }

    async function getContentFileRefsAsync(sessionId: string, scope?: SyncScope) {
        const plain = await syncApi.postPlainAsync(`sessions/${sessionId}/contentfiles`, scope ?? undefined)
        return ((plain ?? []) as any[]).map(plainToContentFileRef) // eslint-disable-line @typescript-eslint/no-explicit-any
    }

    async function submitChangesAsync(sessionId: string, syncId: string, scope: SyncScope, signal?: AbortSignal) {
        await client.submitChangesAsync(sessionId, syncId, scope, signal)
    }

    async function abandonSessionAsync(sessionId: string | undefined) {
        if (!sessionId) {
            return
        }
        dropSession(sessionId)
        // Best-effort teardown: the local session is already dropped and the server reclaims an
        // orphaned session on its own, so a failed destroy must not disrupt the caller's flow -- but
        // trace it, since a persistent failure means sessions are accumulating server-side.
        await destroySessionAsync(sessionId).catch(error => trackException(error, { operation: 'abandonSession' }))
    }

    async function destroyCompletedSessionAsync(sessionId: string | undefined) {
        if (!sessionId) {
            return
        }
        dropSession(sessionId)
        // See abandonSession: server-side teardown is best-effort once the local session is gone.
        await destroySessionAsync(sessionId).catch(error => trackException(error, { operation: 'destroyCompletedSession' }))
    }

    function setDefaultLivingWindowPersons(state: SyncSessionState, graph: WorkspaceSourceGraph) {
        const groups = buildLivingWindowGroups(graph, state.livingStartPersonId)
        const ids: string[] = []
        for (const [category, persons] of groups) {
            if (category != RelationshipCategory.Other) {
                ids.push(...persons.map(p => p.id!))
            }
        }
        state.livingWindowPersonIds = ids
    }

    function selectDefaultLivingWindowPersons(sessionId: string) {
        const state = requireState(sessionId)
        if (state.sourceGraph) {
            setDefaultLivingWindowPersons(state, state.sourceGraph)
        }
    }

    // One sync operation at a time per session -- acquired before any group/workspace creation so two
    // concurrent starts can't create duplicate trees/syncs, and owns the abort controller for the whole op.
    async function runSyncAsync<T>(sessionId: string, run: (abort: AbortController) => Promise<T>) {
        if (uploadAborts.has(sessionId)) {
            throw new Error('A sync is already in progress for this session')
        }
        const abort = new AbortController()
        uploadAborts.set(sessionId, abort)
        try {
            return await run(abort)
        }
        finally {
            uploadAborts.delete(sessionId)
        }
    }

    async function startSyncAsync(sessionId: string) {
        const session = getSession(sessionId)
        if (!session?.id) {
            throw new Error('No session')
        }
        return runSyncAsync(sessionId, async abort => {
            const state = requireState(sessionId)

            const dataGroupStore = useDataGroupStore()
            const dg = new DataGroup()
            dg.name = deriveDataGroupName(sessionId)
            dg.groupType = DataGroupType.Research
            const groupId = await dataGroupStore.addAsync(dg)

            const workspaceStore = useWorkspaceStore()
            const workspace = new Workspace()
            workspace.viewGroupId = groupId
            workspace.dataFileType = session.sourceType
            // The uploaded file name is the web upload's best source-file identity (no path available).
            workspace.dataFilePath = session.sourceFilePath
            workspace.clientDeviceInfo = buildClientDeviceInfo()
            Object.assign(workspace.syncOptions, state.syncOptions)
            workspace.syncOptions.livingStartItemId = state.livingStartPersonId
            const workspaceId = await workspaceStore.addAsync(workspace)

            await setWorkspaceAsync(sessionId, workspaceId)

            return submitSyncAsync(sessionId, groupId, workspaceId, abort)
        })
    }

    // Retry a failed sync from the still-valid session, reusing the tree and workspace already
    // created for it. Nothing is wrong with the file or the choices, so starting over would just
    // make the user redo identical work.
    async function retrySyncAsync(sessionId: string) {
        const workspaceId = getSession(sessionId)?.workspaceId
        if (!workspaceId) {
            throw new Error('No session to retry')
        }
        return runSyncAsync(sessionId, async abort => {
            const workspaceStore = useWorkspaceStore()
            const workspace = await workspaceStore.getAsyncWorkspace(workspaceId, LoadMode.EnsureLoaded)?.whenLoadCompleted
            if (!workspace?.viewGroupId) {
                throw new Error('No workspace for retry')
            }
            return submitSyncAsync(sessionId, workspace.viewGroupId, workspaceId, abort)
        })
    }

    async function resyncWorkspaceAsync(sessionId: string, workspaceId: string) {
        if (!workspaceId) {
            throw new Error('No workspace to update')
        }
        return runSyncAsync(sessionId, async abort => {
            if (getSession(sessionId)?.workspaceId !== workspaceId) {
                await setWorkspaceAsync(sessionId, workspaceId)
            }
            const workspaceStore = useWorkspaceStore()
            const workspace = await workspaceStore.getAsyncWorkspace(workspaceId, LoadMode.EnsureLoaded)?.whenLoadCompleted
            if (!workspace?.viewGroupId) {
                throw new Error('No workspace to update')
            }
            return submitSyncAsync(sessionId, workspace.viewGroupId, workspaceId, abort)
        })
    }

    async function submitSyncAsync(sessionId: string, groupId: string, workspaceId: string, abort: AbortController) {
        const session = getSession(sessionId)!
        const state = requireState(sessionId)

        const syncStore = useSyncStore()
        const sync = new Sync()
        sync.workspaceId = workspaceId
        sync.syncSessionId = sessionId
        sync.syncAgent = session.agent
        sync.syncAgentVersion = session.agentVersion

        const source = new SyncSource()
        source.dataFileType = session.sourceType
        source.dataFilePath = session.sourceFilePath
        source.dataFileSize = session.sourceFileSize
        source.dataFileHash = session.sourceFileHash
        source.modifiedDate = session.sourceFileModifiedDate
        sync.source = source

        if (state.sourceDetails) {
            const sourceDetails = new SyncSourceDetails()
            sourceDetails.itemCounts = { ...state.sourceDetails.itemCounts }
            sync.sourceDetails = sourceDetails
        }

        const newSync = await syncStore.addAsync(sync)
        state.syncId = newSync.id

        const scope = new SyncScope()
        if (state.syncOptions.includeLivingPersons && state.livingWindowPersonIds.length > 0) {
            scope.includeLivingWindowPersonIds = [...state.livingWindowPersonIds]
        }
        scope.includeContent = state.syncOptions.includeContent
        await refreshSessionAsync(sessionId)
        await submitChangesAsync(sessionId, newSync.id!, scope, abort.signal)

        return { dataGroupId: groupId, syncId: newSync.id! }
    }

    // Cancel an in-flight sync: abort the changes upload first -- the server rolls the sync back to
    // Active and deletes the partial blob -- then flag it Cancelled so no Active sync is left dangling.
    async function cancelSyncAsync(sessionId: string) {
        const state = getState(sessionId)
        const syncId = state?.syncId
        uploadAborts.get(sessionId)?.abort()
        if (syncId) {
            // Best-effort: if the sync already raced to a final state, a failed cancel must not throw here.
            await useSyncStore().cancelAsync(syncId).catch(error => trackException(error, { operation: 'cancelSync' }))
        }
        if (state) {
            state.syncId = undefined
        }
    }

    // Recover the sync in flight for this session after a reload: only the workspace's most recent
    // sync can still be active, and it belongs to this session if this session submitted it.
    async function resumeSyncAsync(sessionId: string) {
        const session = await ensureSessionLoadedAsync(sessionId)
        const workspaceId = session?.workspaceId
        if (!workspaceId) {
            return undefined
        }
        const workspaceStore = useWorkspaceStore()
        const workspace = await workspaceStore.getAsyncWorkspace(workspaceId, LoadMode.EnsureLoaded)?.whenLoadCompleted
        const lastSyncId = workspace?.lastSyncId
        if (!lastSyncId) {
            return undefined
        }
        const syncStore = useSyncStore()
        const lastSync = await syncStore.getAsyncSync(lastSyncId, LoadMode.EnsureLoaded)?.whenLoadCompleted
        if (lastSync?.syncSessionId !== sessionId) {
            return undefined
        }
        requireState(sessionId).syncId = lastSyncId
        return lastSyncId
    }

    function deriveDataGroupName(sessionId: string) {
        const path = getSession(sessionId)?.sourceFilePath ?? 'Tree'
        const base = path.split(/[\\/]/).pop() ?? path
        const idx = base.lastIndexOf('.')
        return idx > 0 ? base.slice(0, idx) : base
    }

    function buildClientDeviceInfo() {
        return client.getClientDeviceInfo()
    }

    return {
        asyncSessions,
        sessionStates,
        getState,
        getSession,

        createSessionAsync,
        ensureSessionLoadedAsync,
        refreshSessionAsync,
        uploadFileAsync,
        setWorkspaceAsync,
        loadSourceDetailsAsync,
        compareToWorkspaceAsync,
        loadLivingWindowGraphAsync,
        getContentFileRefsAsync,
        submitChangesAsync,

        dropSession,
        abandonSessionAsync,
        destroyCompletedSessionAsync,
        selectDefaultLivingWindowPersons,
        startSyncAsync,
        cancelSyncAsync,
        retrySyncAsync,
        resyncWorkspaceAsync,
        resumeSyncAsync,
        buildClientDeviceInfo,
    }
})
